import { Completion, CompletionContext, CompletionResult } from '@codemirror/autocomplete';

import {
  DEFAULT_FORMULA_EDITOR_CONFIG,
  FORMULA_CONSTANT_LABELS,
  FORMULA_FUNCTION_LABELS,
  FORMULA_FUNCTIONS_BY_CATEGORY,
  FORMULA_OPERATOR_LABELS,
  FormulaEditorConfig,
  getEffectiveConstants,
  getEffectiveFunctions,
  getEffectiveOperators
} from '../utils/formula-editor-config';

/**
 * Creates a CodeMirror autocomplete source that suggests variable names.
 * Supports hyphenated identifiers (e.g. `my-var-name`).
 */
export function formulaVariableCompletionSource(variableNames: string[]) {
  const options: Completion[] = variableNames.map((name) => ({
    label: name,
    type: 'variable',
    detail: 'variable',
    boost: 2
  }));

  return (context: CompletionContext): CompletionResult | null => {
    const word = context.matchBefore(/[a-zA-Z_][a-zA-Z0-9_-]*/);
    if (!word) {
      // Show all variables on explicit trigger (e.g. Ctrl+Space) even without a word
      if (context.explicit) {
        return { from: context.pos, options, filter: false };
      }
      return null;
    }
    if (word.from === word.to && !context.explicit) {
      return null;
    }
    return {
      from: word.from,
      options,
      filter: true
    };
  };
}

/**
 * Resolves the category label for a function name.
 */

/** Reverse lookup: function name → category. Built once at module init. */
const FUNCTION_TO_CATEGORY: ReadonlyMap<string, string> = (() => {
  const map = new Map<string, string>();
  for (const [category, fns] of Object.entries(FORMULA_FUNCTIONS_BY_CATEGORY)) {
    for (const fn of Object.keys(fns)) {
      map.set(fn, category);
    }
  }
  return map;
})();

function getFunctionCategory(fnName: string): string {
  return FUNCTION_TO_CATEGORY.get(fnName) ?? 'function';
}

/**
 * Creates a CodeMirror autocomplete source that suggests allowed math functions.
 * Inserts function name followed by `(` for convenience.
 * Groups functions by mathjs category in the detail label.
 */
export function formulaFunctionCompletionSource(config: FormulaEditorConfig = DEFAULT_FORMULA_EDITOR_CONFIG) {
  const allowedFunctions = getEffectiveFunctions(config);
  const options: Completion[] = [...allowedFunctions].map((fn) => ({
    label: fn,
    type: 'function',
    detail: `${FORMULA_FUNCTION_LABELS[fn] ?? 'function'} (${getFunctionCategory(fn)})`,
    apply: `${fn}(`,
    boost: 1
  }));

  return (context: CompletionContext): CompletionResult | null => {
    const word = context.matchBefore(/[a-zA-Z_][a-zA-Z0-9_]*/);
    if (!word) {
      // Show all functions on explicit trigger (e.g. Ctrl+Space) even without a word
      if (context.explicit) {
        return { from: context.pos, options, filter: false };
      }
      return null;
    }
    if (word.from === word.to && !context.explicit) {
      return null;
    }
    return {
      from: word.from,
      options,
      filter: true
    };
  };
}

/**
 * Creates a CodeMirror autocomplete source that suggests allowed constants.
 * Constants are inserted as-is (no parentheses).
 */
export function formulaConstantCompletionSource(config: FormulaEditorConfig = DEFAULT_FORMULA_EDITOR_CONFIG) {
  const allowedConstants = getEffectiveConstants(config);
  const options: Completion[] = [...allowedConstants].map((name) => ({
    label: name,
    type: 'constant',
    detail: FORMULA_CONSTANT_LABELS[name] ?? 'constant',
    boost: 0
  }));

  return (context: CompletionContext): CompletionResult | null => {
    const word = context.matchBefore(/[a-zA-Z_][a-zA-Z0-9_]*/);
    if (!word) {
      // Show all constants on explicit trigger (e.g. Ctrl+Space) even without a word
      if (context.explicit) {
        return { from: context.pos, options, filter: false };
      }
      return null;
    }
    if (word.from === word.to && !context.explicit) {
      return null;
    }
    return {
      from: word.from,
      options,
      filter: true
    };
  };
}

/**
 * Creates a CodeMirror autocomplete source that suggests allowed operators.
 * Activated when the cursor is in operator position (after a number, variable, or closing paren).
 */
export function formulaOperatorCompletionSource(config: FormulaEditorConfig = DEFAULT_FORMULA_EDITOR_CONFIG) {
  const allowedOperators = getEffectiveOperators(config);
  const options: Completion[] = [...allowedOperators].map((op) => ({
    label: op,
    type: 'keyword',
    detail: FORMULA_OPERATOR_LABELS[op] ?? 'operator',
    apply: ` ${op} `
  }));

  return (context: CompletionContext): CompletionResult | null => {
    // Match partial operator characters (including bitwise |, &, ~)
    const opMatch = context.matchBefore(/[+\-*/^%><=!&|~]+/);
    if (!opMatch) {
      // Check if we are explicitly requesting completions after a value token
      if (!context.explicit) {
        return null;
      }
      // Only show operators after a word, number, or closing paren
      const before = context.state.doc.sliceString(Math.max(0, context.pos - 1), context.pos);
      if (!/[\w)\d]/.test(before)) {
        return null;
      }
      return {
        from: context.pos,
        options,
        filter: true
      };
    }
    return {
      from: opMatch.from,
      options,
      filter: true
    };
  };
}

/**
 * Creates a combined autocomplete source for variables, functions, constants, and operators.
 * This is the main entry point used by the editor component.
 */
export function formulaAutocompletionSource(variableNames: string[], config: FormulaEditorConfig = DEFAULT_FORMULA_EDITOR_CONFIG) {
  const variableSource = formulaVariableCompletionSource(variableNames);
  const functionSource = formulaFunctionCompletionSource(config);
  const constantSource = formulaConstantCompletionSource(config);
  const operatorSource = formulaOperatorCompletionSource(config);

  return (context: CompletionContext): CompletionResult | null => {
    // ── Normal completion sources ──────────────────────────────────────────
    const fnResult = functionSource(context);
    const varResult = variableSource(context);
    const constResult = constantSource(context);
    const opResult = operatorSource(context);

    // ── Intercept & / && → suggest 'and' ──────────────────────────────────
    // Must be checked FIRST before any other source so that `&` characters
    // never fall through to the operator source (which would show all ops).
    const andTrigger = context.matchBefore(/&+/);
    if (andTrigger) {
      return {
        from: andTrigger.from,
        validFor: /^&*$/,
        options: opResult.options.filter((op) => op.label === 'and'),
        filter: false
      };
    }

    // ── Intercept | / || → suggest 'or' ───────────────────────────────────
    const orTrigger = context.matchBefore(/\|+/);
    if (orTrigger) {
      return {
        from: orTrigger.from,
        validFor: /^\|*$/,
        options: opResult.options.filter((op) => op.label === 'or'),
        filter: false
      };
    }

    // Merge word-based results (functions, variables, constants share the same word position)
    const wordResults = [fnResult, varResult, constResult].filter((r): r is CompletionResult => r !== null);

    if (wordResults.length > 1) {
      const from = wordResults[0].from;
      const allSameFrom = wordResults.every((r) => r.from === from);
      if (allSameFrom) {
        return {
          from,
          options: wordResults.flatMap((r) => r.options),
          filter: true
        };
      }
    }

    // Return whichever matched
    return wordResults[0] ?? opResult;
  };
}

